import { IService } from '@services/IService'
import { Container } from 'inversify'

import { Result } from '@/domain/protocols/Result'
import { UndoRouteUseCase } from '@/domain/useCases/Route/undoRoute'

import { ITrackingService } from '@/services/Tracking/ITrackingService'
import { UserChartSyncService } from '@/services/userChartSync/UserChartSyncService'
import { UserChartTilesPrefetchService } from '@/services/userChartTilesPrefetch/UserChartTilesPrefetchService'
import { UserGeometrySyncService } from '@/services/userGeometrySync/UserGeometrySyncService'

import { IAeroInfoRepository } from '@/data/AeroInfoRepository/IAeroInfoRepository'
import { IAircraftModelRepository } from '@/data/AircraftModelRepository/IAircraftModelRepository'
import { IAirspacesRepository } from '@/data/AirspacesRepository/IAirspacesRepository'
import { FeatureFlagsRepository } from '@/data/FeatureFlagsRepository/FeatureFlagsRepository'
import { IFuelRepository } from '@/data/FuelRepository/IFuelRepository'
import { INavaidRepository } from '@/data/NavaidRepository/INavaidRepository'
import { NotamRepository } from '@/data/NotamRepository/NotamRepository'
import { IUserAircraftsRepository } from '@/data/UserAircraftsRepository/IUserAircraftsRepository'
import { IUserGeometryRepository } from '@/data/UserGeometryRepository/IUserGeometryRepository'
import { IUserRoutesRepository } from '@/data/UserRoutesRepository/IUserRoutesRepository'
import { IUserWaypointsRepository } from '@/data/UserWaypointsRepository/IUserWaypointsRepository'
import { IWBProfileAirplaneRepository } from '@/data/WBProfileAirplaneRepository/IWBProfileAirplaneRepository'

import { SplashScreenInfoStateMutator } from '../presentation/modules/Base/components/SplashScreen/SplashScreenInfoState'
import { InjectionTokens } from './tokens'

import { I18n } from '@/i18n/I18n'
import { IAccountWebClient } from '@/infra/Account/IAccountWebClient'
import { httpAgent } from '@/infra/http/axiosInstance'
import { HTTP_TIMEOUTS } from '@/infra/http/timeouts'
import { withNetworkRetry } from '@/utils/withNetworkRetry'

export class LifeCycle {
  private _container: Container
  private _deferredServices: [IService, string][] = []

  constructor(container: Container) {
    this._container = container
  }

  /**
   * URL usada so para provar conectividade.
   *
   * Fica DELIBERADAMENTE cross-origin, sem passar pelo proxy same-origin: em dev
   * `planning-dev.nexatlas.com` resolve para 127.0.0.1, entao um probe same-origin responderia 200
   * mesmo com a maquina sem internet nenhuma — daria a resposta errada justamente no caso que ele
   * existe para detectar.
   */
  private get _connectivityProbeUrl(): string {
    return import.meta.env.VITE_AERODATA_API_BASEURL || 'https://aerodata.nexatlas.com'
  }

  private async _hasConnectivity(probe: Promise<unknown>): Promise<boolean> {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return false
    }

    try {
      await probe
      return true
    } catch {
      return false
    }
  }

  private async _tryToInit(service: IService, label: string): Promise<void> {
    try {
      await service.init()
    } catch (error) {
      console.error(`LifeCycle: ${label}.init() failed`, error)
    }
  }

  private async _tryToStart(service: IService, label: string): Promise<void> {
    try {
      await service.start()
    } catch (error) {
      console.error(`LifeCycle: ${label}.start() failed`, error)
    }
  }

  async initializeComponents(): Promise<Result<void>> {
    const authenticator = this._container.get<IService>(InjectionTokens.Authenticator)
    const accountWebClient = this._container.get<IAccountWebClient>(InjectionTokens.AccountWebClient)
    const featureFlagsRepository = this._container.get<FeatureFlagsRepository>(InjectionTokens.FeatureFlagsRepository)
    const airspaceRepository = this._container.get<IAirspacesRepository>(InjectionTokens.AirspaceRepository)
    const aeroInfoRepository = this._container.get<IAeroInfoRepository>(InjectionTokens.AeroInfoRepository)
    const navaidRepository = this._container.get<INavaidRepository>(InjectionTokens.NavaidRepository)
    const fuelRepository = this._container.get<IFuelRepository>(InjectionTokens.FuelRepository)
    const userWaypointsRepository = this._container.get<IUserWaypointsRepository>(
      InjectionTokens.UserWaypointsRepository
    )
    const userRoutesRepository = this._container.get<IUserRoutesRepository>(InjectionTokens.UserRoutesRepository)
    const userGeometryRepository = this._container.get<IUserGeometryRepository>(InjectionTokens.UserGeometryRepository)
    const statePersister = this._container.get<IService>(InjectionTokens.StatePersister)
    const i18n = this._container.get<I18n>(InjectionTokens.I18n)

    const undoRouteUseCase = this._container.get<UndoRouteUseCase>(InjectionTokens.UndoRouteUseCase)

    const trackingService = this._container.get<ITrackingService>(InjectionTokens.TrackingService)
    const routeStateSideEffects = this._container.get<IService>(InjectionTokens.RouteStateSideEffects)
    const cruiseAltitudeSuggester = this._container.get<IService>(InjectionTokens.CruiseAltitudeSuggester)

    const aircraftModelRepository = this._container.get<IAircraftModelRepository>(
      InjectionTokens.AircraftModelRepository
    )
    const userAircraftRepository = this._container.get<IUserAircraftsRepository>(
      InjectionTokens.UserAircraftsRepository
    )
    const notamRepository = this._container.get<NotamRepository>(InjectionTokens.NotamRepository)
    const wbProfileAirplaneRepository = this._container.get<IWBProfileAirplaneRepository>(
      InjectionTokens.WBProfileAirplaneRepository
    )
    const userChartSyncService = this._container.get<UserChartSyncService>(InjectionTokens.UserChartSyncService)
    const userChartTilesPrefetchService = this._container.get<UserChartTilesPrefetchService>(
      InjectionTokens.UserChartTilesPrefetchService
    )
    const userGeometrySyncService = this._container.get<UserGeometrySyncService>(
      InjectionTokens.UserGeometrySyncService
    )

    SplashScreenInfoStateMutator.initTask('testingInternetConnection')
    // O probe de conectividade custava 579ms de espera serial antes de qualquer trabalho util.
    // Agora ele corre em paralelo: no caminho felizes o sucesso do `getCurrentUser` ja prova que ha
    // internet e o probe nunca e aguardado; ele so e consultado quando algo falha, para distinguir
    // "sem internet" de "problema na conta". O catch aqui evita unhandled rejection enquanto ninguem
    // aguarda a promise.
    // Usa a instancia dedicada e mantem o `withNetworkRetry` explicito: este probe NAO passa pelo
    // `AxiosHttpClient`, entao nao herda o retry de transporte dele.
    const connectivityProbe = withNetworkRetry(() =>
      httpAgent.head<unknown>(this._connectivityProbeUrl, { timeout: HTTP_TIMEOUTS.interactive })
    )
    connectivityProbe.catch((): void => undefined)
    SplashScreenInfoStateMutator.finishTask('testingInternetConnection')

    SplashScreenInfoStateMutator.initTask('checkingLoginStatus')
    const authenticatorInitResult = (await authenticator.init()) as Result<void>

    if (authenticatorInitResult?.isFailure) {
      return Result.fail('AUTHENTICATION_COOKIE_NOT_FOUND')
    }

    const accountInitResult = await accountWebClient.init()

    if (accountInitResult.isFailure) {
      // Antes de culpar a conta, descobre se o problema e simplesmente falta de internet.
      const hasConnectivity = await this._hasConnectivity(connectivityProbe)
      if (!hasConnectivity) {
        return Result.fail('NO_INTERNET_CONNECTION')
      }

      if (accountInitResult.error === 'ACCOUNT_UNAVAILABLE') {
        return Result.fail('ACCOUNT_UNAVAILABLE')
      } else {
        return Result.fail('ACCOUNT_TOKEN_ERROR')
      }
    }

    await trackingService.init()

    const authenticatorStartResult = (await authenticator.start()) as Result<void>
    if (authenticatorStartResult.isFailure) {
      return Result.fail('SUBSCRIPTION_NOT_OK')
    }

    SplashScreenInfoStateMutator.finishTask('checkingLoginStatus')

    await featureFlagsRepository.init()

    SplashScreenInfoStateMutator.initTask('getWaypointData')
    // O retry de flap de rede saiu daqui e foi para o transporte (`AxiosHttpClient`). Envolver o
    // `Promise.all` era errado por dois motivos: um unico flap refazia os ONZE downloads (incluindo
    // os 25 MB de `waypoints_full`), e na pratica nunca disparava, porque os `init()` devolvem
    // `Result` em vez de lancar — a rejeicao nunca chegava ao `withNetworkRetry`.
    await Promise.all([
      i18n.init(),
      statePersister.init(),
      aeroInfoRepository.init(),
      navaidRepository.init(),
      fuelRepository.init(),
      airspaceRepository.init(),
      userWaypointsRepository.init(),
      userRoutesRepository.init(),
      userAircraftRepository.init(),
      notamRepository.init(),
      // Opens the Dexie store so saved geometries are available on boot (sync is Bead 08).
      userGeometryRepository.init()
    ])

    SplashScreenInfoStateMutator.finishTask('getWaypointData')

    wbProfileAirplaneRepository.init()
    undoRouteUseCase.init()
    statePersister.start()
    trackingService.start()
    notamRepository.start()
    routeStateSideEffects.init()
    cruiseAltitudeSuggester.init() // Don't move this line, it needs to be after the State Side Effects route inits.
    aircraftModelRepository.getRemoteAircraftModels()

    // Os servicos de sync/prefetch NAO entram mais no caminho critico do boot: eles apenas agendam
    // timers (UserChartSyncService.INITIAL_SYNC_DELAY_MS = 8s, idem geometrias) e disparam download
    // de tiles. Ficavam awaited em serie antes do primeiro render, custando ~0,4s por nada.
    // `main.tsx` chama `startBackgroundServices()` depois de montar o App.
    this._deferredServices = [
      [userChartSyncService, 'UserChartSyncService'],
      [userChartTilesPrefetchService, 'UserChartTilesPrefetchService'],
      [userGeometrySyncService, 'UserGeometrySyncService']
    ]

    return Result.ok()
  }

  /**
   * Sobe os servicos que nao precisam estar prontos para o primeiro frame. Chamado apos o render do
   * App, num callback de idle, para nao competir com o mount do mapa. Nunca lanca.
   */
  startBackgroundServices(): void {
    const services = this._deferredServices
    this._deferredServices = []

    const run = () => {
      void (async () => {
        for (const [service, label] of services) {
          await this._tryToInit(service, label)
          await this._tryToStart(service, label)
        }
      })()
    }

    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(run, { timeout: 2000 })
    } else {
      setTimeout(run, 0)
    }
  }
}
